import type { BasicAnalyticsEvent } from '../../core/types/Analytics'

export const TEMPLATE_NAMES = {
  BillAmountNotStandard: 'BillAmountNotStandard',
  CanadianUpcomingDeadlineAlert: 'CanadianUpcomingDeadlineAlert',
  DuplicatePaymentV2: 'DuplicatePaymentV2',
  IntroducePulse: 'IntroducePulse',
  LargeTransaction: 'LargeTransaction',
  MonthlySpendToIncomeComparisonV2: 'MonthlySpendToIncomeComparisonV2',
  QuarterlySummary: 'QuarterlySummary',
  SpendingPlanCategorySpending: 'SpendingPlanCategorySpending',
  SubscriptionPriceIncrease: 'SubscriptionPriceIncrease',
  UncategorizedTransactionsV2: 'UncategorizedTransactionsV2',
  UpcomingBill: 'UpcomingBill',
  UnifiedDeposit: 'UnifiedDeposit',
  WeeklySpendingPlanSummary: 'WeeklySpendingPlanSummary',
  WeeklyNewMerchantsV2: 'WeeklyNewMerchantsV2',
  MonthlySpendComparison: 'MonthlySpendComparison',
  MonthlySpendToIncomeComparison: 'MonthlySpendToIncomeComparison',
}

const instanceId = (window as any)?.app?.clientConfig?.microwidget_instance_id || ''

const PATHS = {
  INSIGHTS_MICRO_WIDGET: '/micro-insights-carousel',
}

export const ON_CLICK_EVENT = (name: string) => `on${name}Click`
export const ON_VIEW_EVENT = (name: string) => `on${name}View`
export const ON_VIEW_FIRST_TIME_EVENT = (name: string) => `on${name}ViewFirstTime`
export const readableTemplateName = (name: string) => name.replace(/([a-z])([A-Z])/g, '$1 $2')
export const onBeatClickedEvents = () => {
  return Object.values(TEMPLATE_NAMES).reduce(
    (acc, name) => ({
      ...acc,
      [ON_CLICK_EVENT(name)]: {
        action: ANALYTICS_ACTIONS.MICRO_BEAT_CLICKED,
        category: ANALYTICS_CATEGORIES.BEAT_INTERACTION,
        label: readableTemplateName(name),
        value: instanceId,
      },
    }),
    {},
  ) as Record<keyof AnalyticsEventsType, string>
}

export const onBeatDisplayedEvents = () => {
  return Object.values(TEMPLATE_NAMES).reduce(
    (acc, name) => ({
      ...acc,
      [ON_VIEW_EVENT(name)]: {
        action: ANALYTICS_ACTIONS.MICRO_BEAT_DISPLAYED,
        category: ANALYTICS_CATEGORIES.BEAT_INTERACTION,
        label: readableTemplateName(name),
        value: instanceId,
      },
    }),
    {},
  ) as Record<keyof AnalyticsEventsType, string>
}

export const onBeatDisplayedForFirstTimeEvents = () => {
  return Object.values(TEMPLATE_NAMES).reduce(
    (acc, name) => ({
      ...acc,
      [ON_VIEW_FIRST_TIME_EVENT(name)]: {
        action: ANALYTICS_ACTIONS.MICRO_BEAT_DISPLAYED_FIRST_TIME,
        category: ANALYTICS_CATEGORIES.BEAT_INTERACTION,
        label: readableTemplateName(name),
        value: instanceId,
      },
    }),
    {},
  ) as Record<keyof AnalyticsEventsType, string>
}

export const ANALYTICS_LABELS = {
  CARD: 'Card',
  CAROUSEL_CONTROL: 'Carousel Control',
  MICRO_WIDGET: 'Micro Widget',
  VIEW_MORE: 'View More',
}

export const ANALYTICS_CATEGORIES = {
  MICRO_WIDGET_INTERACTION: 'Micro Widget Interaction',
  BEAT_INTERACTION: 'Beat Interaction',
}

export const ANALYTICS_CLICK_EVENTS = {
  ON_CAROUSEL_BACK_CLICK: 'onCarouselBackClick',
  ON_CAROUSEL_BACK_SWIPE: 'onCarouselBackSwipe',
  ON_CAROUSEL_FORWARD_CLICK: 'onCarouselForwardClick',
  ON_CAROUSEL_FORWARD_SWIPE: 'onCarouselForwardSwipe',
  ON_NO_RELEVANT_INSIGHTS_CLICKED: 'onNoRelevantInsightsClicked',
  ON_VIEW_MORE_CLICKED: 'onViewMoreClicked',
}

export const ANALYTICS_VIEW_EVENTS = {
  ON_VIEW_MORE_VIEWED: 'onViewMoreCardView',
}

export const ANALYTICS_ACTIONS = {
  CONTROL_BACKWARD: 'Control Backward',
  CONTROL_FORWARD: 'Control Forward',
  MICRO_BEAT_CLICKED: 'Micro Beat Clicked',
  MICRO_BEAT_DISPLAYED: 'Micro Beat Displayed',
  MICRO_BEAT_DISPLAYED_FIRST_TIME: 'Micro Beat Displayed For First Time',
  SWIPE_BACKWARD: 'Swipe Backward',
  SWIPE_FORWARD: 'Swipe Forward',
}

export const ANALYTICS_PAGE_NAMES = {
  MICRO_WIDGET_CAROUSEL: 'Micro Widget Carousel',
  MICRO_WIDGET_CAROUSEL_ZERO_STATE: 'Micro Widget Carousel Zero State',
  MICRO_WIDGET_CAROUSEL_NO_RELEVANT_INSIGHTS: 'Micro Widget Carousel No Relevant Insights',
}

export const ANALYTICS_PAGEVIEWS = {
  [ANALYTICS_PAGE_NAMES.MICRO_WIDGET_CAROUSEL]: {
    label: ANALYTICS_LABELS.MICRO_WIDGET,
    name: ANALYTICS_PAGE_NAMES.MICRO_WIDGET_CAROUSEL,
    path: PATHS.INSIGHTS_MICRO_WIDGET,
    value: instanceId,
  },
  // zero state
  [ANALYTICS_PAGE_NAMES.MICRO_WIDGET_CAROUSEL_ZERO_STATE]: {
    label: ANALYTICS_LABELS.MICRO_WIDGET,
    name: ANALYTICS_PAGE_NAMES.MICRO_WIDGET_CAROUSEL_ZERO_STATE,
    path: PATHS.INSIGHTS_MICRO_WIDGET,
    value: instanceId,
  },
  // no relevant cards
  [ANALYTICS_PAGE_NAMES.MICRO_WIDGET_CAROUSEL_NO_RELEVANT_INSIGHTS]: {
    label: ANALYTICS_LABELS.MICRO_WIDGET,
    name: ANALYTICS_PAGE_NAMES.MICRO_WIDGET_CAROUSEL_NO_RELEVANT_INSIGHTS,
    path: '/micro-widget',
    value: instanceId,
  },
}

export const ANALYTICS_EVENTS = {
  // carousel control interaction events - clicks and swipes
  [ANALYTICS_CLICK_EVENTS.ON_CAROUSEL_BACK_CLICK]: {
    action: ANALYTICS_ACTIONS.CONTROL_BACKWARD,
    category: ANALYTICS_CATEGORIES.MICRO_WIDGET_INTERACTION,
    label: ANALYTICS_LABELS.CAROUSEL_CONTROL,
    value: instanceId,
  },
  [ANALYTICS_CLICK_EVENTS.ON_CAROUSEL_FORWARD_CLICK]: {
    action: ANALYTICS_ACTIONS.CONTROL_FORWARD,
    category: ANALYTICS_CATEGORIES.MICRO_WIDGET_INTERACTION,
    label: ANALYTICS_LABELS.CAROUSEL_CONTROL,
    value: instanceId,
  },
  [ANALYTICS_CLICK_EVENTS.ON_CAROUSEL_BACK_SWIPE]: {
    action: ANALYTICS_ACTIONS.SWIPE_BACKWARD,
    category: ANALYTICS_CATEGORIES.MICRO_WIDGET_INTERACTION,
    label: ANALYTICS_LABELS.CAROUSEL_CONTROL,
    value: instanceId,
  },
  [ANALYTICS_CLICK_EVENTS.ON_CAROUSEL_FORWARD_SWIPE]: {
    action: ANALYTICS_ACTIONS.SWIPE_FORWARD,
    category: ANALYTICS_CATEGORIES.MICRO_WIDGET_INTERACTION,
    label: ANALYTICS_LABELS.CAROUSEL_CONTROL,
    value: instanceId,
  },
  // read more card events
  [ANALYTICS_CLICK_EVENTS.ON_VIEW_MORE_CLICKED]: {
    action: ANALYTICS_ACTIONS.MICRO_BEAT_CLICKED,
    category: ANALYTICS_CATEGORIES.BEAT_INTERACTION,
    label: ANALYTICS_LABELS.VIEW_MORE,
    value: instanceId,
  },
  [ANALYTICS_VIEW_EVENTS.ON_VIEW_MORE_VIEWED]: {
    action: ANALYTICS_ACTIONS.MICRO_BEAT_DISPLAYED,
    category: ANALYTICS_CATEGORIES.BEAT_INTERACTION,
    label: ANALYTICS_LABELS.VIEW_MORE,
    value: instanceId,
  },

  ...onBeatClickedEvents(),
  ...onBeatDisplayedEvents(),
  ...onBeatDisplayedForFirstTimeEvents(),
}

export interface AnalyticsEventsObjectType extends BasicAnalyticsEvent {
  value: string
}

export type AnalyticsEventsType = {
  [key: string]: AnalyticsEventsObjectType
}
